import { logRuntimeError } from "Diagnostics/runtimeLogger";
import { getShopPayText as buildShopPayText } from "./shopPay";
var ShopifyPageType = /* @__PURE__ */ ((ShopifyPageType2) => {
  ShopifyPageType2["Home"] = "home";
  ShopifyPageType2["Product"] = "product";
  ShopifyPageType2["Collection"] = "collection";
  ShopifyPageType2["Collections"] = "collections";
  ShopifyPageType2["Vendors"] = "vendors";
  ShopifyPageType2["Page"] = "page";
  ShopifyPageType2["Cart"] = "cart";
  return ShopifyPageType2;
})(ShopifyPageType || {});
export function publishWebpixelEvent(eventName, data) {
  try {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, data);
    }
  } catch (error) {
    logRuntimeError({
      source: "shopify:publishWebpixelEvent",
      error,
      extra: { "event.name": eventName }
    });
  }
}
export function getPageType() {
  const convermaxPage = window.Convermax.Shopify?.pageType;
  if (convermaxPage) {
    switch (convermaxPage) {
      case "index":
        return "home" /* Home */;
      case "collection":
        return window.Convermax.Shopify?.collectionVendor ? (
          // vendor collection page (/collections/vendors?q=)
          "vendors" /* Vendors */
        ) : "collection" /* Collection */;
      case "list-collections":
        return "collections" /* Collections */;
      default:
        return toKnownPageType(convermaxPage);
    }
  }
  return toKnownPageType(window.ShopifyAnalytics?.meta?.page?.pageType);
}
function toKnownPageType(value) {
  if (!value) {
    return void 0;
  }
  const known = Object.values(ShopifyPageType).includes(value);
  return known ? value : void 0;
}
function coerceId(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : void 0;
  }
  if (typeof value === "string" && value !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : void 0;
  }
  return void 0;
}
export function getProductId() {
  return coerceId(window.Convermax.Shopify?.productId ?? window.ShopifyAnalytics?.meta?.product?.id);
}
export function getProductType() {
  return window.Convermax.Shopify?.productType ?? window.ShopifyAnalytics?.meta?.product?.type;
}
export function getVariantId() {
  const urlVariantId = new URLSearchParams(window.location.search).get("variant");
  return coerceId(urlVariantId ?? void 0) ?? coerceId(
    window.Convermax.Shopify?.selectedVariantId ?? window.ShopifyAnalytics?.meta?.product?.variants?.[0]?.id
  );
}
export function getCollectionId() {
  return coerceId(
    window.Convermax.Shopify?.collectionId ?? (getPageType() === "collection" /* Collection */ ? window.ShopifyAnalytics?.meta?.page?.resourceId : void 0)
  );
}
export function getPageId() {
  return coerceId(
    window.Convermax.Shopify?.pageId ?? (getPageType() === "page" /* Page */ ? window.ShopifyAnalytics?.meta?.page?.resourceId : void 0)
  );
}
export function getCustomerId() {
  return coerceId(
    window.Convermax.Shopify?.customerId ?? window.ShopifyAnalytics?.meta?.page?.customerId ?? window.meta?.page?.customerId ?? window.__st?.cid
  );
}
export function getCustomerTags() {
  return window.Convermax.Shopify?.customerTags;
}
export function getCompanyId() {
  return window.Convermax.Shopify?.companyId;
}
export function getCompanyLocationId() {
  return window.Convermax.Shopify?.companyLocationId;
}
export function getCountry() {
  return window.Convermax.Shopify?.country ?? window.Shopify?.country;
}
export function getLocale() {
  return window.Convermax.Shopify?.locale ?? window.Shopify?.locale;
}
export function getCurrency() {
  return window.Convermax.Shopify?.currency ?? window.Shopify?.currency?.active;
}
export function getCurrencyRate() {
  return window.Shopify?.currency?.rate;
}
export function getRootRoute() {
  return (window.Convermax.Shopify?.rootRoute ?? window.Shopify?.routes?.root ?? "").replace(/\/+$/, "");
}
export function getCartUrls() {
  const cmShopify = window.Convermax.Shopify;
  const rootRoute = getRootRoute();
  return {
    root: cmShopify?.cartUrl ?? `${rootRoute}/cart`,
    add: cmShopify?.cartAddUrl ?? `${rootRoute}/cart/add`,
    change: cmShopify?.cartChangeUrl ?? `${rootRoute}/cart/change`,
    clear: cmShopify?.cartClearUrl ?? `${rootRoute}/cart/clear`,
    update: cmShopify?.cartUpdateUrl ?? `${rootRoute}/cart/update`
  };
}
export function getSearchPageUrl() {
  return window.Convermax.Shopify?.searchPageUrl ?? `${getRootRoute()}/pages/search`;
}
export function getCollectionsPageUrl() {
  return window.Convermax.Shopify?.collectionsUrl ?? `${getRootRoute()}/collections`;
}
function optionalString(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}
export function getThemeInfo() {
  const theme = window.Shopify?.theme;
  if (!theme || typeof theme !== "object") {
    return void 0;
  }
  const raw = theme;
  const result = {};
  const id = coerceId(raw.id);
  if (id !== void 0) {
    result.id = id;
  }
  const name = optionalString(raw.name);
  if (name !== void 0) {
    result.name = name;
  }
  const role = optionalString(raw.role);
  if (role !== void 0) {
    result.role = role;
  }
  const schemaName = optionalString(raw.schema_name);
  if (schemaName !== void 0) {
    result.schema_name = schemaName;
  }
  const schemaVersion = optionalString(raw.schema_version);
  if (schemaVersion !== void 0) {
    result.schema_version = schemaVersion;
  }
  return result;
}
export function getShopPayText(price, variantId, available, shopPayTerms) {
  return buildShopPayText(price, variantId, available, shopPayTerms, getCountry(), getCurrency());
}
export const isInDesignMode = !!window.Convermax.Shopify?.isInDesignMode;
const shopifyHelpers = {
  getCartUrls,
  getCollectionsPageUrl,
  getCollectionId,
  getCompanyId,
  getCompanyLocationId,
  getCountry,
  getCurrency,
  getCustomerId,
  getCustomerTags,
  isInDesignMode,
  getLocale,
  getPageId,
  getPageType,
  getProductId,
  getProductType,
  getRootRoute,
  getSearchPageUrl,
  getShopPayText,
  getThemeInfo,
  getVariantId,
  publishWebpixelEvent
};
export function getStoragePermissions() {
  try {
    const privacy = window.Shopify?.customerPrivacy;
    return {
      analytics: privacy?.analyticsProcessingAllowed() ?? null,
      functional: privacy ? privacy.preferencesProcessingAllowed() : true
    };
  } catch {
    return { analytics: null, functional: null };
  }
}
export function initializePlatformHelpers() {
  window.Convermax.Shopify = {
    ...window.Convermax.Shopify || {},
    ...shopifyHelpers
  };
}
export function initializePlatformStorage(onStoragePermissionsChanged) {
  window.document.addEventListener("visitorConsentCollected", onStoragePermissionsChanged);
  return new Promise((resolve) => {
    const onPrivacyLoaded = (error) => {
      if (!error) {
        onStoragePermissionsChanged();
      }
      resolve();
    };
    if (!window.Shopify?.loadFeatures) {
      resolve();
      return;
    }
    const loadPrivacyApi = (version, callback) => {
      try {
        window.Shopify?.loadFeatures?.([{ name: "consent-tracking-api", version }], callback);
      } catch (error) {
        logRuntimeError({ source: "shopify:loadFeatures", error });
        resolve();
      }
    };
    loadPrivacyApi("0.2", async (error) => {
      if (error) {
        await Promise.resolve();
        if (window.Shopify?.loadFeatures) {
          loadPrivacyApi("0.1", onPrivacyLoaded);
        } else {
          onPrivacyLoaded(error);
        }
        return;
      }
      onPrivacyLoaded();
    });
    const resolveIfLoaderIsQueued = () => {
      if (Array.isArray(window.Shopify?.loadFeatures?.q)) {
        resolve();
      }
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", resolveIfLoaderIsQueued, { once: true });
    } else {
      resolveIfLoaderIsQueued();
    }
  });
}
const platformHelpers = {
  initializePlatformHelpers,
  initializePlatformStorage,
  getStoragePermissions
};
export default platformHelpers;
